import { 
  Connection, 
  PublicKey, 
  LAMPORTS_PER_SOL, 
  Transaction, 
  TransactionInstruction,
  SendOptions,
  ConfirmOptions,
  AccountInfo,
  ParsedAccountData,
  Commitment
} from '@solana/web3.js'
import { WalletContextState } from '@solana/wallet-adapter-react'
import BN from 'bn.js'
import { getCTOProgramId } from './anchorClient'

/**
 * Network configuration constants
 * Defines the different Solana network endpoints
 */
export const NETWORK_URLS = {
  'mainnet-beta': 'https://api.mainnet-beta.solana.com',
  'devnet': 'https://api.devnet.solana.com',
  'testnet': 'https://api.testnet.solana.com',
  'localnet': 'http://127.0.0.1:8899',
} as const

export type Network = keyof typeof NETWORK_URLS

/**
 * Default transaction confirmation options
 * Ensures reliable transaction processing
 */
export const DEFAULT_CONFIRM_OPTIONS: ConfirmOptions = {
  commitment: 'confirmed',
  preflightCommitment: 'confirmed',
  skipPreflight: false,
  maxRetries: 3,
}

export const DEFAULT_SEND_OPTIONS: SendOptions = {
  skipPreflight: false,
  preflightCommitment: 'confirmed',
  maxRetries: 3,
}

/**
 * Account state interface for tracking account loading and errors
 */
export interface AccountState<T> {
  data: T | null
  loading: boolean
  error: Error | null
  lastFetched: number | null
}

/**
 * Creates a new Connection instance for the specified network
 * Includes retry logic and proper error handling
 */
export function createConnection(network: Network = 'devnet'): Connection {
  const endpoint = NETWORK_URLS[network]
  return new Connection(endpoint, 'confirmed')
}

/**
 * Gets the current network from a connection endpoint
 * Useful for displaying current network in UI
 */
export function getNetworkFromConnection(connection: Connection): Network {
  const endpoint = connection.rpcEndpoint
  
  for (const [network, url] of Object.entries(NETWORK_URLS)) {
    if (endpoint.includes(url) || endpoint === url) {
      return network as Network
    }
  }
  
  // Default to devnet if unable to determine
  return 'devnet'
}

/**
 * Get SOL balance for a wallet address
 * Returns balance in SOL (not lamports) for easier display
 */
export async function getWalletBalance(
  connection: Connection, 
  publicKey: PublicKey
): Promise<number> {
  try {
    const balance = await connection.getBalance(publicKey)
    return balance / LAMPORTS_PER_SOL
  } catch (error) {
    console.error('Error fetching wallet balance:', error)
    return 0
  }
}

/**
 * Get detailed account information including data and executable status
 * Returns null if account doesn't exist
 */
export async function getAccountInfo(
  connection: Connection,
  publicKey: PublicKey,
  commitment: Commitment = 'confirmed'
): Promise<AccountInfo<Buffer> | null> {
  try {
    return await connection.getAccountInfo(publicKey, commitment)
  } catch (error) {
    console.error('Error fetching account info:', error)
    return null
  }
}

/**
 * Check if an account exists on the blockchain
 * More efficient than fetching full account info when you only need existence
 */
export async function accountExists(
  connection: Connection,
  publicKey: PublicKey
): Promise<boolean> {
  try {
    const accountInfo = await connection.getAccountInfo(publicKey, 'confirmed')
    return accountInfo !== null
  } catch (error) {
    console.error('Error checking account existence:', error)
    return false
  }
}

/**
 * Fetch multiple accounts in a single RPC call
 * More efficient than individual getAccountInfo calls
 */
export async function getMultipleAccounts(
  connection: Connection,
  publicKeys: PublicKey[],
  commitment: Commitment = 'confirmed'
): Promise<(AccountInfo<Buffer> | null)[]> {
  try {
    const response = await connection.getMultipleAccountsInfo(publicKeys, commitment)
    return response
  } catch (error) {
    console.error('Error fetching multiple accounts:', error)
    return new Array(publicKeys.length).fill(null)
  }
}

/**
 * Send and confirm a transaction with retry logic
 * Handles common transaction failures and provides detailed error reporting
 */
export async function sendAndConfirmTransaction(
  connection: Connection,
  transaction: Transaction,
  wallet: WalletContextState,
  options?: SendOptions
): Promise<string> {
  if (!wallet.publicKey || !wallet.signTransaction) {
    throw new Error('Wallet not connected or does not support signing')
  }

  try {
    // Get recent blockhash
    const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed')
    transaction.recentBlockhash = blockhash
    transaction.lastValidBlockHeight = lastValidBlockHeight

    // Set fee payer
    transaction.feePayer = wallet.publicKey

    // Sign transaction
    const signedTransaction = await wallet.signTransaction(transaction)

    // Send transaction
    const signature = await connection.sendRawTransaction(
      signedTransaction.serialize(),
      options || DEFAULT_SEND_OPTIONS
    )

    // Confirm transaction
    const confirmation = await connection.confirmTransaction({
      signature,
      blockhash,
      lastValidBlockHeight,
    }, 'confirmed')

    if (confirmation.value.err) {
      throw new Error(`Transaction failed: ${JSON.stringify(confirmation.value.err)}`)
    }

    return signature
  } catch (error: any) {
    console.error('Transaction error:', error)
    
    // Provide more helpful error messages
    if (error.message?.includes('insufficient funds')) {
      throw new Error('Insufficient SOL balance to complete transaction')
    } else if (error.message?.includes('block height exceeded')) {
      throw new Error('Transaction expired. Please try again.')
    } else if (error.message?.includes('User rejected')) {
      throw new Error('Transaction was rejected by user')
    }
    
    throw new Error(`Transaction failed: ${error.message || 'Unknown error'}`)
  }
}

/**
 * Create a simple transfer instruction
 * Useful for sending SOL between accounts
 */
export async function createTransferInstruction(
  from: PublicKey,
  to: PublicKey,
  lamports: number
): Promise<TransactionInstruction> {
  const { SystemProgram } = await import('@solana/web3.js')
  
  return SystemProgram.transfer({
    fromPubkey: from,
    toPubkey: to,
    lamports,
  })
}

/**
 * Helper to find all Program Derived Addresses for the CTO program
 * Useful for discovering related accounts
 */
export class PDAgenerateHelper {
  private static get programId() {
    return getCTOProgramId()
  }

  /**
   * Generate pool PDA for a given token mint
   * Each token can only have one pool
   */
  static getPoolPDA(tokenMint: PublicKey): [PublicKey, number] {
    return PublicKey.findProgramAddressSync(
      [Buffer.from('pool'), tokenMint.toBuffer()],
      this.programId
    )
  }

  /**
   * Generate donor PDA for a pool and wallet combination
   * Tracks individual donor participation in pools
   */
  static getDonorPDA(pool: PublicKey, wallet: PublicKey): [PublicKey, number] {
    return PublicKey.findProgramAddressSync(
      [Buffer.from('donor'), pool.toBuffer(), wallet.toBuffer()],
      this.programId
    )
  }

  /**
   * Generate proposal PDA for a pool
   * Each pool can have only one active proposal at a time
   */
  static getProposalPDA(pool: PublicKey): [PublicKey, number] {
    return PublicKey.findProgramAddressSync(
      [Buffer.from('proposal'), pool.toBuffer()],
      this.programId
    )
  }

  /**
   * Generate vote record PDA for a proposal and voter
   * Stores individual voting records
   */
  static getVoteRecordPDA(proposal: PublicKey, voter: PublicKey): [PublicKey, number] {
    return PublicKey.findProgramAddressSync(
      [Buffer.from('vote'), proposal.toBuffer(), voter.toBuffer()],
      this.programId
    )
  }

  /**
   * Generate all donor PDAs for a specific wallet
   * Useful for finding all pools a user has participated in
   */
  static async findDonorPDAsForWallet(
    connection: Connection,
    wallet: PublicKey
  ): Promise<PublicKey[]> {
    try {
      // Get all program accounts that match the donor pattern
      const accounts = await connection.getProgramAccounts(this.programId, {
        filters: [
          {
            memcmp: {
              offset: 40, // Skip discriminator (8) + pool pubkey (32)
              bytes: wallet.toBase58(),
            },
          },
        ],
      })

      return accounts.map((account: any) => account.pubkey)
    } catch (error) {
      console.error('Error finding donor PDAs for wallet:', error)
      return []
    }
  }

  /**
   * Batch validate multiple PDAs
   * Checks if the provided addresses are valid PDAs for their respective seeds
   */
  static validatePDAs(pdas: Array<{ address: PublicKey; seeds: (Buffer | PublicKey)[] }>): boolean[] {
    return pdas.map(({ address, seeds }) => {
      try {
        const seedBuffers = seeds.map(seed => 
          seed instanceof PublicKey ? seed.toBuffer() : seed
        )
        const [derivedAddress] = PublicKey.findProgramAddressSync(seedBuffers, this.programId)
        return derivedAddress.equals(address)
      } catch {
        return false
      }
    })
  }
}

/**
 * Utility class for working with SPL tokens
 * Provides common token operations needed for the dApp
 */
export class TokenUtils {
  /**
   * Get token mint information
   * Returns metadata about a token mint including supply and decimals
   */
  static async getMintInfo(connection: Connection, mint: PublicKey) {
    try {
      const { TOKEN_PROGRAM_ID: _TOKEN_PROGRAM_ID } = await import('@solana/spl-token')
      const mintInfo = await connection.getParsedAccountInfo(mint)
      
      if (!mintInfo.value) {
        throw new Error('Mint not found')
      }

      const data = mintInfo.value.data as ParsedAccountData
      return data.parsed.info
    } catch (error) {
      console.error('Error fetching mint info:', error)
      return null
    }
  }

  /**
   * Validate if a PublicKey is a valid SPL token mint
   * Checks if the account exists and is owned by the Token Program
   */
  static async isValidTokenMint(connection: Connection, mint: PublicKey): Promise<boolean> {
    try {
      const accountInfo = await connection.getAccountInfo(mint)
      if (!accountInfo) return false

      // Check if owned by Token Program
      const { TOKEN_PROGRAM_ID } = await import('@solana/spl-token')
      return accountInfo.owner.equals(TOKEN_PROGRAM_ID)
    } catch (error) {
      console.error('Error validating token mint:', error)
      return false
    }
  }

  /**
   * Format token amount with proper decimals
   * Converts raw token amount to human-readable format
   */
  static formatTokenAmount(amount: BN | number, decimals: number): string {
    const amountBN = typeof amount === 'number' ? new BN(amount) : amount
    const divisor = new BN(10).pow(new BN(decimals))
    const quotient = amountBN.div(divisor)
    const remainder = amountBN.mod(divisor)
    
    if (remainder.isZero()) {
      return quotient.toString()
    }

    const remainderStr = remainder.toString().padStart(decimals, '0')
    const trimmedRemainder = remainderStr.replace(/0+$/, '')
    
    return trimmedRemainder ? `${quotient.toString()}.${trimmedRemainder}` : quotient.toString()
  }
}

/**
 * Utility functions for formatting and displaying blockchain data
 * Provides consistent formatting across the application
 */
export class DisplayUtils {
  /**
   * Format a PublicKey for display
   * Shows first and last characters with ellipsis in between
   */
  static formatPublicKey(pubkey: PublicKey | string, length: number = 8): string {
    const key = typeof pubkey === 'string' ? pubkey : pubkey.toBase58()
    if (key.length <= length * 2) return key
    return `${key.slice(0, length)}...${key.slice(-length)}`
  }

  /**
   * Format SOL amount with appropriate decimal places
   * Handles very small and very large amounts gracefully
   */
  static formatSOL(lamports: BN | number, decimals: number = 4): string {
    const sol = typeof lamports === 'number' ? lamports / LAMPORTS_PER_SOL : lamports.toNumber() / LAMPORTS_PER_SOL
    
    if (sol === 0) return '0'
    if (sol < 0.0001) return '<0.0001'
    if (sol >= 1000000) return (sol / 1000000).toFixed(2) + 'M'
    if (sol >= 1000) return (sol / 1000).toFixed(2) + 'K'
    
    return sol.toFixed(decimals).replace(/\.?0+$/, '')
  }

  /**
   * Format percentage values
   * Handles edge cases and provides consistent display
   */
  static formatPercentage(value: number, decimals: number = 2): string {
    if (value === 0) return '0%'
    if (value < 0.01) return '<0.01%'
    return `${value.toFixed(decimals)}%`
  }

  /**
   * Format timestamp to human-readable date
   * Converts Unix timestamp to localized date string
   */
  static formatTimestamp(timestamp: BN | number, includeTime: boolean = true): string {
    const ts = typeof timestamp === 'number' ? timestamp : timestamp.toNumber()
    const date = new Date(ts * 1000) // Convert to milliseconds
    
    if (includeTime) {
      return date.toLocaleString()
    }
    return date.toLocaleDateString()
  }

  /**
   * Format time remaining until a deadline
   * Returns human-readable relative time
   */
  static formatTimeRemaining(deadline: BN | number): string {
    const deadlineTs = typeof deadline === 'number' ? deadline : deadline.toNumber()
    const now = Math.floor(Date.now() / 1000)
    const remaining = deadlineTs - now

    if (remaining <= 0) return 'Expired'

    const days = Math.floor(remaining / 86400)
    const hours = Math.floor((remaining % 86400) / 3600)
    const minutes = Math.floor((remaining % 3600) / 60)

    if (days > 0) return `${days}d ${hours}h`
    if (hours > 0) return `${hours}h ${minutes}m`
    return `${minutes}m`
  }

  /**
   * Format large numbers with appropriate suffixes
   * Makes large values more readable
   */
  static formatLargeNumber(value: BN | number): string {
    const num = typeof value === 'number' ? value : value.toNumber()
    
    if (num >= 1e9) return (num / 1e9).toFixed(1) + 'B'
    if (num >= 1e6) return (num / 1e6).toFixed(1) + 'M'
    if (num >= 1e3) return (num / 1e3).toFixed(1) + 'K'
    return num.toString()
  }
}

/**
 * Error handling utilities for Solana operations
 * Provides user-friendly error messages
 */
export class SolanaErrorUtils {
  /**
   * Parse Solana transaction errors into user-friendly messages
   * Handles common error patterns and provides helpful suggestions
   */
  static parseTransactionError(error: any): string {
    const message = error?.message || error?.toString() || 'Unknown error'

    // Common error patterns and their user-friendly messages
    const errorPatterns = [
      {
        pattern: /insufficient funds/i,
        message: 'Insufficient SOL balance for this transaction. Please add more SOL to your wallet.'
      },
      {
        pattern: /Transaction was not confirmed/i,
        message: 'Transaction failed to confirm. Please try again.'
      },
      {
        pattern: /User rejected/i,
        message: 'Transaction was rejected. Please approve the transaction to continue.'
      },
      {
        pattern: /block height exceeded/i,
        message: 'Transaction expired. Please try again.'
      },
      {
        pattern: /Account does not exist/i,
        message: 'Required account does not exist. This pool may not be initialized yet.'
      },
      {
        pattern: /custom program error: 0x0/i,
        message: 'Math overflow error. The requested amount is too large.'
      },
      {
        pattern: /custom program error: 0x1/i,
        message: 'Amount must be greater than zero.'
      },
      {
        pattern: /custom program error: 0x2/i,
        message: 'You have no shares in this pool.'
      },
      {
        pattern: /custom program error: 0x3/i,
        message: 'Withdrawal amount exceeds your available balance.'
      },
      {
        pattern: /custom program error: 0x4/i,
        message: 'Insufficient free liquidity. Funds may be reserved for an active proposal.'
      },
      {
        pattern: /custom program error: 0x5/i,
        message: 'There is already an active proposal for this pool.'
      },
      {
        pattern: /custom program error: 0x6/i,
        message: 'You need at least 1 SOL worth of shares to create a proposal.'
      },
      {
        pattern: /custom program error: 0x7/i,
        message: 'No active proposal to vote on.'
      },
      {
        pattern: /custom program error: 0x8/i,
        message: 'Voting period has ended for this proposal.'
      },
      {
        pattern: /custom program error: 0x9/i,
        message: 'You are not eligible to vote on this proposal.'
      },
      {
        pattern: /custom program error: 0xa/i,
        message: 'Too early to execute this proposal. Please wait for the voting period to end.'
      }
    ]

    for (const { pattern, message: friendlyMessage } of errorPatterns) {
      if (pattern.test(message)) {
        return friendlyMessage
      }
    }

    return `Transaction failed: ${message}`
  }

  /**
   * Check if an error is retryable
   * Helps determine if the user should be encouraged to retry
   */
  static isRetryableError(error: any): boolean {
    const message = error?.message || error?.toString() || ''
    
    const retryablePatterns = [
      /block height exceeded/i,
      /Transaction was not confirmed/i,
      /Timeout/i,
      /Network error/i,
      /fetch.*failed/i
    ]

    return retryablePatterns.some(pattern => pattern.test(message))
  }
}